import { Button } from '@/app/common/components/button/Button'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { currencyFormatter } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import CurrencyInput from 'react-currency-input-field'
import { z } from 'zod'
import type {
  ProgramPurseResource,
  Purse,
} from '@/queries/program_purses/schemas.ts'
import { useProgramPursesQuery } from '@/queries/program_purses/useProgramPursesQuery.ts'
import { useTransferPurseFunds } from '@/queries/program_purses/useTransferPurseFunds.ts'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { useForm } from 'react-hook-form'

interface TransferFundsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  purse: ProgramPurseResource
  programId: number
}

type TransferFundsFormValues = {
  amount: string
  targetProgramPurseId: string
}

export function TransferFundsDialog({
  open,
  onOpenChange,
  purse,
  programId,
}: TransferFundsDialogProps) {
  const { toast } = useToast()

  const transferFundsSchema = z.object({
    amount: z
      .string()
      .min(1, 'Amount is required')
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Amount must be a positive number',
      })
      .refine((val) => Number(val) <= purse.balance_available, {
        message: `Amount cannot exceed ${currencyFormatter.format(
          purse.balance_available
        )}`,
      }),
    targetProgramPurseId: z.string().min(1, 'Target purse is required'),
  })

  const form = useForm<TransferFundsFormValues>({
    resolver: zodResolver(transferFundsSchema),
    defaultValues: {
      amount: '',
      targetProgramPurseId: '',
    },
  })

  const pursesQuery = useProgramPursesQuery({ programId }, open)

  const targetPurses: Purse[] = (pursesQuery.data ?? []).filter(
    (programPurse) => programPurse.id !== purse.id
  )

  const selectedTargetPurse = targetPurses.find(
    (programPurse) =>
      programPurse.id.toString() === form.watch('targetProgramPurseId')
  )

  const transferMutation = useTransferPurseFunds({
    onSuccess: (_, variables) => {
      const targetPurseName =
        targetPurses.find(
          (programPurse) => programPurse.id === variables.targetProgramPurseId
        )?.name ?? 'selected purse'

      toast({
        variant: 'default',
        title: 'Transfer Successful',
        description: `Successfully transferred ${currencyFormatter.format(
          variables.amount
        )} from purse ${purse.name} to ${targetPurseName}.`,
      })

      form.reset()
      onOpenChange(false)
    },
    onError: (error: RequestError) => {
      toast({
        variant: 'destructive',
        title: 'Transfer Failed',
        description: error.message,
      })
    },
  })

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  const onSubmit = (values: TransferFundsFormValues) => {
    transferMutation.mutate({
      programId,
      purseId: purse.id,
      amount: Number(values.amount.replace(/[^\d.-]+/g, '')),
      targetProgramPurseId: Number(values.targetProgramPurseId),
    })
  }

  const transferAmount =
    Number(form.watch('amount')?.replace(/[^\d.-]+/g, '')) || 0

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
          return
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer Funds</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="min-w-0 space-y-4"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="transfer-amount"
                      name={field.name}
                      decimalsLimit={2}
                      min={1}
                      prefix="$"
                      value={field.value}
                      onBlur={field.onBlur}
                      onValueChange={(value) => {
                        field.onChange(value || '')
                      }}
                      placeholder="Enter amount"
                      required
                      className={inputVariants({
                        variant: 'text',
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="targetProgramPurseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transfer To</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="target-program-purse">
                        <SelectValue
                          placeholder={
                            pursesQuery.isLoading
                              ? 'Loading purses...'
                              : 'Choose a purse'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {targetPurses.map((programPurse) => (
                          <SelectItem
                            key={programPurse.id}
                            value={programPurse.id.toString()}
                          >
                            <span className="flex items-center gap-1">
                              <span className="truncate max-w-48">
                                {programPurse.name}
                              </span>
                              <span className="shrink-0 text-gray-500">
                                ({programPurse.id})
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="overflow-hidden bg-gray-50 p-4 rounded-lg space-y-2">
              <h4 className="font-medium text-gray-900">Transfer Summary</h4>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Available Balance:</span>
                <span className="font-medium">
                  {currencyFormatter.format(purse.balance_available)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Transfer Amount:</span>
                <span className="font-medium">
                  {currencyFormatter.format(transferAmount)}
                </span>
              </div>

              <div className="flex justify-between gap-4 text-sm">
                <span className="shrink-0 text-gray-600">Transfer To:</span>
                <span className="min-w-0 font-medium">
                  {selectedTargetPurse ? (
                    <span className="flex items-center gap-1">
                      <span className="truncate">
                        {selectedTargetPurse.name}
                      </span>
                      <span className="shrink-0 text-gray-500">
                        ({selectedTargetPurse.id})
                      </span>
                    </span>
                  ) : (
                    'Not selected'
                  )}
                </span>
              </div>

              <div className="border-t pt-2 flex justify-between">
                <span className="font-medium text-gray-900">
                  Remaining Balance:
                </span>
                <span className="font-bold text-green-600">
                  {currencyFormatter.format(
                    Math.max(purse.balance_available - transferAmount, 0)
                  )}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <SubmitButton
                buttonTxt="Transfer Funds"
                status={transferMutation.status}
              />
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
